type ApiErrorKind = 'validation' | 'conflict' | 'server' | 'network'

type ProblemDetails = {
  title?: string
  detail?: string
  errors?: Record<string, string[]>
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status?: number
  readonly details?: unknown

  constructor(kind: ApiErrorKind, message: string, status?: number, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.kind = kind
    this.status = status
    this.details = details
  }
}

function buildUrl(path: string) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '')

  return baseUrl ? `${baseUrl}${normalizedPath}` : normalizedPath
}

function logDevelopmentApiError(url: string, status?: number, body?: unknown, error?: unknown) {
  if (!import.meta.env.DEV) {
    return
  }

  console.error('ToneSignature API request failed', {
    url,
    status,
    responseBody: body,
    networkError: error,
  })
}

function getProblemMessage(problem: ProblemDetails | undefined, fallback: string) {
  const validationMessage = problem?.errors ? Object.values(problem.errors).flat().join(' ') : ''
  return validationMessage || problem?.detail || problem?.title || fallback
}

async function parseResponseBody(response: Response) {
  if (response.status === 204) {
    return undefined
  }

  const text = await response.text()
  if (!text) {
    return undefined
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

async function request<TResponse>(path: string, init: RequestInit): Promise<TResponse> {
  let response: Response
  const url = buildUrl(path)

  try {
    response = await fetch(url, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error
    }

    logDevelopmentApiError(url, undefined, undefined, error)
    throw new ApiError('network', 'Network failure.', undefined, error)
  }

  const body = await parseResponseBody(response)

  if (response.ok) {
    return body as TResponse
  }

  logDevelopmentApiError(url, response.status, body)

  const problem = typeof body === 'object' && body !== null ? (body as ProblemDetails) : undefined

  if (response.status === 400) {
    throw new ApiError(
      'validation',
      getProblemMessage(problem, 'The request is invalid.'),
      response.status,
      body,
    )
  }

  if (response.status === 409) {
    throw new ApiError(
      'conflict',
      getProblemMessage(problem, 'The request conflicts with an existing resource.'),
      response.status,
      body,
    )
  }

  if (response.status === 500) {
    throw new ApiError(
      'server',
      getProblemMessage(problem, 'An unexpected server error occurred.'),
      response.status,
      body,
    )
  }

  throw new ApiError('server', getProblemMessage(problem, 'An unexpected API error occurred.'), response.status, body)
}

export function get<TResponse>(path: string, signal?: AbortSignal) {
  return request<TResponse>(path, { method: 'GET', signal })
}

export function post<TRequest, TResponse>(path: string, body: TRequest, signal?: AbortSignal) {
  return request<TResponse>(path, {
    method: 'POST',
    body: JSON.stringify(body),
    signal,
  })
}
